const STORAGE_KEY = 'comprehendpt_questions_cache';
const MAX_NOTES = 10;
let fallbackStoreLock: Promise<void> = Promise.resolve();

export type CapturedNote = {
  patientInfo: any;
  pageInfo: any;
  noteKey: string;
  questions: Record<string, any>;
  postWriteQuestions?: Record<string, any>;
  /*
  The note as scraped immediately BEFORE Comprehend first wrote each captured
  section. A section follows the latest capture until its Comprehend boundary;
  sections first seen later can still join the snapshot. It is distinct from
  `questions` (the latest scrape) and `postWriteQuestions` (what Comprehend
  wrote). Absent means unknown (a note first seen after Comprehend had written,
  or stored by an older version) and must not be inferred from the other two; a
  section that was on screen but empty is kept as an empty value.
  */
  initialQuestions?: Record<string, any>;
  initialQuestionsFrozen?: boolean;
  initialQuestionFrozenPaths?: string[][];
  lastCapturedQuestionPaths?: string[][];
  aiGeneratedAt?: number;
  regenerationCount?: number;
  conversationId?: number;
  configId?: number;
  signedOffAt?: number;
  captureShape?: string;
  modifiedAt: number;
  createdAt: number;
  dirtySince?: number;
  uploaded: boolean;
};

export type NoteStore = {
  entries: Record<string, CapturedNote>;
};

type SaveCapturedNoteOptions = {
  conversationId?: number;
  configId?: number;
  generatedAnswers?: Record<string, any>;
  signedOffAt?: number;
  captureShape?: string;
  mergeDepth?: number;
};

/*
A content script orphaned by an extension reload keeps `chrome` but loses
chrome.runtime.id, which looks exactly like the embed, iPad and iPhone builds
that legitimately have no extension APIs. Remember which one we started as.

An extension that has lost its runtime must stop rather than fall through to the
page's own localStorage: notes written there belong to the EMR's origin, nothing
can ever upload them, and note content has no business being left behind on a
customer's domain. Losing the in-progress note is the cheaper failure — capture
rebuilds from the DOM, so the next page load re-reads whatever is on screen.
*/
const startedInExtension = hasExtensionRuntime();

function isOrphanedExtension() {
  return startedInExtension && !hasExtensionRuntime();
}

export function hasNoteStorage() {
  if (isOrphanedExtension()) return false;
  if (hasExtensionRuntime()) return hasChromeStorage();
  return typeof localStorage !== 'undefined';
}

export async function saveCapturedNote(
  emrKey: string,
  patientInfo: any,
  pageInfo: any,
  noteKey: string,
  capturedQuestions: Record<string, any>,
  options: SaveCapturedNoteOptions = {}
) {
  return withStoreLock(async () => {
    const {
      conversationId,
      configId,
      generatedAnswers,
      signedOffAt,
      captureShape,
      mergeDepth = 1
    } = options;
    const key = getNoteCacheKey(emrKey, noteKey);
    const store = await readNoteStore();
    const previous = store.entries[key];

    if (
      previous?.conversationId !== undefined &&
      conversationId !== undefined &&
      previous.conversationId !== conversationId
    ) {
      return previous;
    }
    /*
    Merging assumes every snapshot arranges the note the same way, so a path
    absent from this one means "that section is not on screen" and is kept.

    An injector that can arrange the same note more than one way says which
    arrangement this snapshot uses, and a change replaces the note rather than
    accumulating beside it. Empower is the case: it nests SOAP under one section
    or splits it into five depending on the per-user empowerNewVersion setting,
    which the injector only learns once the panel scrapes. Polls before that use
    the other arrangement, and without this the note keeps both — the earlier
    copies, never emitted again, frozen as stale duplicates of the same text.

    Sections dropped this way return on the next poll that renders them. An
    injector that omits captureShape (every other EMR) merges exactly as before.

    mergeDepth lets an injector whose sections render in parts (Raintree shows
    one sub-tab of a section at a time) merge that many levels deep instead of
    replacing the whole section. The default of 1 is the top-level merge above.
    */
    const shapeChanged = Boolean(
      captureShape &&
        previous?.captureShape &&
        previous.captureShape !== captureShape
    );
    let questions = shapeChanged
      ? { ...capturedQuestions }
      : mergeQuestions(previous?.questions || {}, capturedQuestions, mergeDepth);
    const recordsComprehend = conversationId !== undefined;
    const linksConversation = conversationId !== undefined && previous?.conversationId === undefined;
    const storedConversationId = previous?.conversationId ?? conversationId;
    const storedConfigId = configId ?? previous?.configId;
    let regenerationCount = previous?.regenerationCount;
    if (recordsComprehend) {
      if (linksConversation) {
        regenerationCount = 0;
      } else {
        regenerationCount = (previous?.regenerationCount ?? 0) + 1;
      }
    }
    const updatesBaseline = generatedAnswers !== undefined && hasGeneratedAnswers(generatedAnswers);
    /*
    Until the first Comprehend run, the original follows the latest note using
    the same merge as `questions`. That makes a note which is never comprehended
    have identical initial and final values. The Comprehend flow explicitly
    freezes the last pre-write scrape; the conversation link is a fallback for
    older callers which do not send that boundary.
    */
    const comprehendHasWritten =
      recordsComprehend || previous?.conversationId !== undefined || updatesBaseline;
    let frozenPaths = previous?.initialQuestionFrozenPaths || [];
    if (
      frozenPaths.length === 0 &&
      previous?.initialQuestionsFrozen &&
      previous.initialQuestions
    ) {
      // Cache entries written by the whole-note freeze treated every existing
      // top-level section as frozen. Preserve that meaning after upgrading.
      frozenPaths = capturePaths(previous.initialQuestions, 1);
    }
    if (
      recordsComprehend &&
      frozenPaths.length === 0 &&
      previous?.initialQuestions
    ) {
      // Older callers may link without sending the pre-write freeze message.
      frozenPaths =
        previous.lastCapturedQuestionPaths || capturePaths(previous.questions, 1);
    }
    let storedInitialQuestions = previous?.initialQuestions;
    const isOrdinaryCapture =
      !recordsComprehend && generatedAnswers === undefined;
    if (
      isOrdinaryCapture &&
      (storedInitialQuestions !== undefined || !comprehendHasWritten)
    ) {
      if (!(shapeChanged && frozenPaths.length > 0)) {
        storedInitialQuestions = updateUnfrozenPaths(
          shapeChanged ? {} : storedInitialQuestions || {},
          capturedQuestions,
          mergeDepth,
          frozenPaths
        );
      }
    }
    const initialQuestionsFrozen = frozenPaths.length > 0 ? true : undefined;
    const lastCapturedQuestionPaths = capturePaths(capturedQuestions, mergeDepth);
    // A baseline recorded against the old arrangement cannot intersect this one.
    let storedPostWriteQuestions = shapeChanged
      ? undefined
      : previous?.postWriteQuestions;
    let aiGeneratedAt = previous?.aiGeneratedAt;
    /*
    Sign-off marks the note but never closes it to further capture.

    It is detected from a click, and in every EMR that click can still be
    cancelled or blocked by a confirmation the injector cannot see (WebPT 1.0
    raises a native confirm(); WebPT 2.0 can open a "Confirm Date of Service"
    dialog). Freezing the note on that click meant one mis-read click stopped
    capture for the note forever and reported a draft as final. Staying open is
    self-correcting — the backend upserts, so the next capture fixes the record —
    and it also keeps capturing addenda to a genuinely signed note.

    The latest explicit sign-off wins; ordinary polls pass undefined and keep it.
    */
    const storedSignedOffAt = signedOffAt ?? previous?.signedOffAt;
    if (updatesBaseline) {
      storedPostWriteQuestions = updatePostWriteBaseline(
        previous?.postWriteQuestions,
        capturedQuestions,
        generatedAnswers
      );
      if (aiGeneratedAt === undefined) {
        aiGeneratedAt = Date.now();
      }
    }
    const storedContent = {
      patientInfo,
      pageInfo,
      questions,
      conversationId: storedConversationId,
      configId: storedConfigId,
      postWriteQuestions: storedPostWriteQuestions,
      initialQuestions: storedInitialQuestions,
      initialQuestionsFrozen,
      initialQuestionFrozenPaths: frozenPaths.length > 0 ? frozenPaths : undefined,
      lastCapturedQuestionPaths,
      aiGeneratedAt,
      regenerationCount,
      signedOffAt: storedSignedOffAt,
      captureShape: captureShape ?? previous?.captureShape
    };
    if (
      previous &&
      serializeStoredContent(previous) === serializeStoredContent(storedContent)
    ) {
      return previous;
    }

    const now = Math.max(Date.now(), (previous?.modifiedAt ?? 0) + 1);
    const wasUploaded = previous?.uploaded === true;
    let dirtySince = previous?.dirtySince ?? previous?.createdAt ?? now;
    if (wasUploaded || linksConversation || updatesBaseline) {
      dirtySince = now;
    }

    const note: CapturedNote = {
      patientInfo,
      pageInfo,
      noteKey,
      questions,
      modifiedAt: now,
      createdAt: previous?.createdAt ?? now,
      dirtySince,
      uploaded: false
    };
    if (storedConversationId !== undefined) {
      note.conversationId = storedConversationId;
    }
    if (storedConfigId !== undefined) {
      note.configId = storedConfigId;
    }
    if (storedPostWriteQuestions !== undefined) {
      note.postWriteQuestions = storedPostWriteQuestions;
    }
    if (storedInitialQuestions !== undefined) {
      note.initialQuestions = storedInitialQuestions;
    }
    if (initialQuestionsFrozen) {
      note.initialQuestionsFrozen = true;
    }
    if (frozenPaths.length > 0) {
      note.initialQuestionFrozenPaths = frozenPaths;
    }
    note.lastCapturedQuestionPaths = lastCapturedQuestionPaths;
    if (aiGeneratedAt !== undefined) {
      note.aiGeneratedAt = aiGeneratedAt;
    }
    if (regenerationCount !== undefined) {
      note.regenerationCount = regenerationCount;
    }
    if (storedSignedOffAt !== undefined) {
      note.signedOffAt = storedSignedOffAt;
    }
    const storedCaptureShape = captureShape ?? previous?.captureShape;
    if (storedCaptureShape !== undefined) {
      note.captureShape = storedCaptureShape;
    }

    store.entries[key] = note;
    pruneStore(store);
    await storageSet(STORAGE_KEY, store);
    return note;
  });
}

function hasGeneratedAnswers(generatedAnswers: Record<string, any>) {
  return Object.values(generatedAnswers).some((answer) => {
    if (isRecord(answer)) {
      return hasGeneratedAnswers(answer);
    }
    return answer !== null && answer !== undefined;
  });
}

function updatePostWriteBaseline(
  previousBaseline: Record<string, any> | undefined,
  capturedQuestions: Record<string, any>,
  generatedAnswers: Record<string, any>
) {
  // Generated answers provide targeted field paths only; the post-write EMR
  // scrape provides the values. Preserve fields this run did not target.
  const baseline: Record<string, any> = { ...(previousBaseline || {}) };

  Object.entries(generatedAnswers).forEach(([key, generatedAnswer]) => {
    if (generatedAnswer === null || generatedAnswer === undefined) return;

    const capturedValue = capturedQuestions[key];
    const capturedHasKey = Object.prototype.hasOwnProperty.call(
      capturedQuestions,
      key
    );
    if (isRecord(generatedAnswer)) {
      if (capturedHasKey && !isRecord(capturedValue)) {
        baseline[key] = capturedValue;
        return;
      }

      let capturedFields = {};
      if (isRecord(capturedValue)) {
        capturedFields = capturedValue;
      }
      let previousFields;
      if (isRecord(baseline[key])) {
        previousFields = baseline[key];
      }
      const selectedValue = updatePostWriteBaseline(
        previousFields,
        capturedFields,
        generatedAnswer
      );
      if (Object.keys(selectedValue).length > 0) {
        baseline[key] = selectedValue;
      } else {
        delete baseline[key];
      }
      return;
    }

    if (capturedHasKey) {
      baseline[key] = capturedValue;
    } else {
      delete baseline[key];
    }
  });

  return baseline;
}

// Depth 1 is a plain top-level merge; each extra level merges one level deeper
// before values are replaced.
function mergeQuestions(
  previous: Record<string, any>,
  captured: Record<string, any>,
  depth: number
): Record<string, any> {
  const merged = { ...previous };
  Object.entries(captured).forEach(([key, value]) => {
    merged[key] =
      depth > 1 && isRecord(value) && isRecord(previous[key])
        ? mergeQuestions(previous[key], value, depth - 1)
        : value;
  });
  return merged;
}

function capturePaths(
  captured: Record<string, any>,
  depth: number,
  prefix: string[] = []
): string[][] {
  const paths: string[][] = [];
  Object.entries(captured).forEach(([key, value]) => {
    const path = [...prefix, key];
    if (depth > 1 && isRecord(value) && Object.keys(value).length > 0) {
      paths.push(...capturePaths(value, depth - 1, path));
    } else {
      paths.push(path);
    }
  });
  return paths;
}

function updateUnfrozenPaths(
  previous: Record<string, any>,
  captured: Record<string, any>,
  depth: number,
  frozenPaths: string[][],
  prefix: string[] = []
): Record<string, any> {
  const updated = cloneValue(previous);
  Object.entries(captured).forEach(([key, value]) => {
    const path = [...prefix, key];
    if (depth > 1 && isRecord(value) && Object.keys(value).length > 0) {
      const previousValue = isRecord(updated[key]) ? updated[key] : {};
      updated[key] = updateUnfrozenPaths(
        previousValue,
        value,
        depth - 1,
        frozenPaths,
        path
      );
    } else if (
      !frozenPaths.some((frozenPath) => pathsIntersect(frozenPath, path))
    ) {
      updated[key] = cloneValue(value);
    }
  });
  return updated;
}

function pathsIntersect(left: string[], right: string[]) {
  const sharedLength = Math.min(left.length, right.length);
  return left.slice(0, sharedLength).every((part, index) => part === right[index]);
}

function unionPaths(existing: string[][], additions: string[][]) {
  const paths = existing.map((path) => [...path]);
  const seen = new Set(paths.map((path) => JSON.stringify(path)));
  additions.forEach((path) => {
    const key = JSON.stringify(path);
    if (!seen.has(key)) {
      paths.push([...path]);
      seen.add(key);
    }
  });
  return paths;
}

function cloneValue<T>(value: T): T {
  return value === undefined ? value : JSON.parse(JSON.stringify(value));
}

function isRecord(value: any): value is Record<string, any> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

export function getNoteCacheKey(emrKey: string, noteKey: string) {
  return JSON.stringify({
    emr: emrKey,
    noteKey
  });
}

export function getCapturedNotes() {
  return withStoreLock(() => readNoteStore());
}

export function freezeCapturedNoteInitialQuestions(
  emrKey: string,
  noteKey: string
) {
  return withStoreLock(async () => {
    const store = await readNoteStore();
    const note = store.entries[getNoteCacheKey(emrKey, noteKey)];
    if (!note) return note;

    const paths = note.lastCapturedQuestionPaths || capturePaths(note.questions, 1);
    const frozenPaths = unionPaths(note.initialQuestionFrozenPaths || [], paths);
    if (
      note.initialQuestionsFrozen &&
      frozenPaths.length === note.initialQuestionFrozenPaths?.length
    ) {
      return note;
    }

    const now = Math.max(Date.now(), note.modifiedAt + 1);
    note.initialQuestions ??= cloneValue(note.questions);
    note.initialQuestionsFrozen = true;
    note.initialQuestionFrozenPaths = frozenPaths;
    note.dirtySince = note.uploaded
      ? now
      : note.dirtySince ?? note.createdAt;
    note.modifiedAt = now;
    note.uploaded = false;
    await storageSet(STORAGE_KEY, store);
    return note;
  });
}

export function markCapturedNoteSignedOff(
  emrKey: string,
  noteKey: string,
  signedOffAt: number
) {
  return withStoreLock(async () => {
    const store = await readNoteStore();
    const key = getNoteCacheKey(emrKey, noteKey);
    const note = store.entries[key];
    if (!note || note.signedOffAt === signedOffAt) return note;

    note.signedOffAt = signedOffAt;
    note.modifiedAt = Math.max(signedOffAt, note.modifiedAt + 1);
    note.dirtySince = signedOffAt;
    note.uploaded = false;
    await storageSet(STORAGE_KEY, store);
    return note;
  });
}

export function markNotesUploaded(
  uploadedEntries: Array<[string, CapturedNote]>
) {
  return withStoreLock(async () => {
    const store = await storageGet<NoteStore>(STORAGE_KEY);
    if (!store?.entries) return;

    uploadedEntries.forEach(([cacheKey, uploadedNote]) => {
      const currentNote = store.entries[cacheKey];
      if (
        currentNote &&
        serializeStoredContent(currentNote) ===
          serializeStoredContent(uploadedNote)
      ) {
        currentNote.uploaded = true;
        delete currentNote.dirtySince;
      }
    });
    return storageSet(STORAGE_KEY, store);
  });
}

async function readNoteStore(): Promise<NoteStore> {
  const stored = await storageGet<NoteStore>(STORAGE_KEY);
  const store = stored?.entries ? stored : { entries: {} };
  let changed = false;
  Object.values(store.entries).forEach((note) => {
    const legacyNote = note as Partial<CapturedNote> & {
      uploadedAt?: number;
      contentHash?: string;
    };
    if (legacyNote.uploaded === undefined) {
      note.uploaded = false;
      if (
        legacyNote.uploadedAt !== undefined &&
        legacyNote.uploadedAt >= note.modifiedAt
      ) {
        note.uploaded = true;
      }
      delete legacyNote.uploadedAt;
      delete legacyNote.contentHash;
      changed = true;
    }
  });
  if (pruneStore(store)) changed = true;
  if (changed) {
    await storageSet(STORAGE_KEY, store);
  }
  return store;
}

/*
Key-order-independent JSON.

chrome.storage.local serializes through Chromium's base::Value dictionaries,
which keep keys sorted, so a note read back from storage has alphabetised keys
while a freshly scraped one is in insertion order. Comparing those with plain
JSON.stringify reports "changed" on every poll for identical content — which
re-saved the note forever, so its modifiedAt never aged and the settled-note
upload could never fire. Canonicalise both sides instead.
*/
function stableStringify(value: any): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value) ?? 'null';
  }
  if (Array.isArray(value)) {
    return `[${value
      .map((item) => (item === undefined ? 'null' : stableStringify(item)))
      .join(',')}]`;
  }
  // Matches JSON.stringify, which omits undefined-valued properties.
  const keys = Object.keys(value)
    .filter((key) => value[key] !== undefined)
    .sort();
  return `{${keys
    .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
    .join(',')}}`;
}

function serializeStoredContent(note: Partial<CapturedNote>) {
  return stableStringify({
    patientInfo: note.patientInfo,
    pageInfo: note.pageInfo,
    questions: note.questions,
    conversationId: note.conversationId,
    configId: note.configId,
    postWriteQuestions: note.postWriteQuestions,
    initialQuestions: note.initialQuestions,
    initialQuestionsFrozen: note.initialQuestionsFrozen,
    initialQuestionFrozenPaths: note.initialQuestionFrozenPaths,
    lastCapturedQuestionPaths: note.lastCapturedQuestionPaths,
    aiGeneratedAt: note.aiGeneratedAt,
    regenerationCount: note.regenerationCount,
    signedOffAt: note.signedOffAt,
    captureShape: note.captureShape
  });
}

function pruneStore(store: NoteStore) {
  const oldEntries = Object.entries(store.entries)
    .sort(([, a], [, b]) => b.modifiedAt - a.modifiedAt)
    .slice(MAX_NOTES);

  oldEntries.forEach(([key]) => delete store.entries[key]);
  return oldEntries.length > 0;
}

function withStoreLock<T>(callback: () => Promise<T>): Promise<T> {
  if (globalThis.navigator?.locks) {
    return globalThis.navigator.locks.request(STORAGE_KEY, callback);
  }

  const operation = fallbackStoreLock.then(callback, callback);
  fallbackStoreLock = operation.then(
    () => undefined,
    () => undefined
  );
  return operation;
}

function storageGet<T>(key: string): Promise<T | undefined> {
  if (isOrphanedExtension()) return Promise.resolve(undefined);
  if (!hasChromeStorage()) {
    if (hasExtensionRuntime()) return Promise.resolve(undefined);
    try {
      const value = localStorage.getItem(key);
      return Promise.resolve(value ? JSON.parse(value) : undefined);
    } catch (_) {
      return Promise.resolve(undefined);
    }
  }

  return new Promise((resolve) => {
    chrome.storage.local.get([key], (result) => resolve(result?.[key]));
  });
}

function storageSet(key: string, value: any): Promise<void> {
  if (isOrphanedExtension()) return Promise.resolve();
  if (!hasChromeStorage()) {
    if (hasExtensionRuntime()) return Promise.resolve();
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (_) {
      return Promise.resolve();
    }
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    chrome.storage.local.set({ [key]: value }, () => resolve());
  });
}

function hasChromeStorage() {
  return Boolean(
    hasExtensionRuntime() && chrome.storage?.local
  );
}

function hasExtensionRuntime() {
  return Boolean(typeof chrome !== 'undefined' && chrome.runtime?.id);
}
